import axios from "axios";
import {
  useQueries,
  useQueryClient,
  UseQueryResult,
} from "@tanstack/react-query";
import { useRef } from "react";
import { DLQItem, Stage } from "../lusca-search-api/generated-src";
import { getAppSetting } from "../config/AppSettings";
import { DlqInfo } from "./useFetchDlqInfo";

interface BatchDlqResult {
  status: "PENDING" | "COMPLETED" | "ERROR";
  queryExecutionId?: string;
  dlqByOrder?: Record<
    string,
    {
      inDLQ: boolean;
      dlqItems: DLQItem[];
    }
  >;
}

const MAX_BATCH_ORDER_IDS = 100;
const loadingDlqInfo: DlqInfo = { status: "loading", error: null };

function chunkOrderIds(orderIds: string[]): string[][] {
  const chunks: string[][] = [];
  for (let index = 0; index < orderIds.length; index += MAX_BATCH_ORDER_IDS) {
    chunks.push(orderIds.slice(index, index + MAX_BATCH_ORDER_IDS));
  }
  return chunks;
}

/** One Athena query per at-most-100 resolved orders in a UAT Project. */
export default function useBatchDlqInfo(
  stage: Stage,
  transactionIds: string[]
): Record<string, DlqInfo> {
  const queryClient = useQueryClient();
  const restartedQueries = useRef(new Set<string>());
  const uniqueOrderIds = Array.from(new Set(transactionIds)).sort();
  const orderIdChunks = chunkOrderIds(uniqueOrderIds);

  const batchQueries = useQueries({
    queries: orderIdChunks.map((chunk) => {
      const orderIds = chunk.join(",");
      const queryKey = ["dlqInfoBatch", stage, orderIds];
      const restartKey = `${stage}:${orderIds}`;

      return {
        queryKey,
        queryFn: async (): Promise<BatchDlqResult> => {
          const previous = queryClient.getQueryData<BatchDlqResult>(queryKey);
          const apiUrl = getAppSetting("apiUrl") as string;
          const response = await axios.get<BatchDlqResult>(
            `https://${apiUrl}/jwt/dlq-info`,
            {
              params: {
                stage,
                orderIds,
                queryExecutionId: previous?.queryExecutionId,
              },
            }
          );
          return response.data;
        },
        enabled: Boolean(stage && orderIds),
        refetchOnWindowFocus: false,
        retry: false,
        refetchInterval: (result: BatchDlqResult | undefined) =>
          result?.status === "PENDING" ? 1000 : false,
        onSuccess: (result: BatchDlqResult) => {
          if (result.status === "COMPLETED") {
            restartedQueries.current.delete(restartKey);
            return;
          }
          if (result.status !== "ERROR" || !result.queryExecutionId) {
            return;
          }

          queryClient.setQueryData<BatchDlqResult>(queryKey, (current) =>
            current
              ? { ...current, queryExecutionId: undefined }
              : { ...result, queryExecutionId: undefined }
          );
          if (!restartedQueries.current.has(restartKey)) {
            restartedQueries.current.add(restartKey);
            queryClient.invalidateQueries(queryKey).catch(() => undefined);
          }
        },
        onError: () => {
          queryClient.setQueryData<BatchDlqResult>(queryKey, {
            status: "ERROR",
            queryExecutionId: undefined,
          });
          restartedQueries.current.delete(restartKey);
        },
      };
    }),
  }) as UseQueryResult<BatchDlqResult, Error>[];

  const dlqInfoByOrder = batchQueries.reduce<Record<string, DlqInfo>>(
    (byOrder, query, index) => {
      const chunk = orderIdChunks[index] ?? [];
      const error =
        query.error instanceof Error
          ? query.error
          : query.data?.status === "ERROR"
          ? new Error("DLQ query failed")
          : null;
      if (error) {
        return {
          ...byOrder,
          ...Object.fromEntries(
            chunk.map((orderId) => [orderId, { status: "error", error }])
          ),
        };
      }
      if (query.data?.status !== "COMPLETED") {
        return byOrder;
      }
      return {
        ...byOrder,
        ...Object.fromEntries(
          Object.entries(query.data.dlqByOrder ?? {}).map(
            ([orderId, result]) => [
              orderId,
              {
                status: result.inDLQ ? "inDlq" : "notInDlq",
                dlqItems: result.dlqItems,
                error: null,
              },
            ]
          )
        ),
      };
    },
    {}
  );

  return transactionIds.reduce<Record<string, DlqInfo>>(
    (byOrder, transactionId) => ({
      ...byOrder,
      [transactionId]: dlqInfoByOrder[transactionId] ?? loadingDlqInfo,
    }),
    {}
  );
}
